import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Search, ChevronDown, Eye, Edit, X, Calendar, Trash2,
  Clock, User, Phone, MapPin, RefreshCw, Wrench
} from 'lucide-react';
import { adminFetch } from '../../utils/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BOOKING_STATUSES = [
  { id: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { id: 'confirmed', label: 'Confirmed', color: 'bg-blue-100 text-blue-800' },
  { id: 'in_progress', label: 'In Progress', color: 'bg-indigo-100 text-indigo-800' },
  { id: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' },
  { id: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' }
];

const SERVICE_TYPES = [
  { id: 'installation', label: 'Installation' },
  { id: 'repair', label: 'Repair' },
  { id: 'maintenance', label: 'Maintenance' },
  { id: 'filter-change', label: 'Filter Change' },
  { id: 'amc', label: 'AMC Service' }
];

function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [cityOptions, setCityOptions] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState({
    status: '',
    assigned_technician: '',
    scheduled_date: '',
    scheduled_time: '',
    admin_notes: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(null);

  useEffect(() => {
    fetchBookings();
  }, [statusFilter, serviceFilter, cityFilter]);

  useEffect(() => {
    // Load list of published city pages so admin can filter
    fetch(`${BACKEND_URL}/api/city-pages`).then(r => r.ok ? r.json() : []).then(list => {
      setCityOptions(Array.isArray(list) ? list.map(p => p.city_name).filter(Boolean) : []);
    }).catch(() => {});
  }, []);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/admin/bookings?limit=100`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (serviceFilter) url += `&service_type=${serviceFilter}`;
      if (cityFilter) url += `&city=${encodeURIComponent(cityFilter)}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const res = await adminFetch(url);
      if (res.ok) {
        const data = await res.json();
        setBookings(data.bookings || []);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBookings();
  };

  const handleStatusUpdate = async () => {
    if (!showStatusModal || !statusUpdate.status) return;
    
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/bookings/${showStatusModal.id}/status`, {
        method: 'PUT',
        body: JSON.stringify(statusUpdate)
      });
      
      if (res.ok) {
        toast.success('Booking status updated');
        fetchBookings();
        setShowStatusModal(null);
        setStatusUpdate({ status: '', assigned_technician: '', scheduled_date: '', scheduled_time: '', admin_notes: '' });
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (bookingId) => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/bookings/${bookingId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Booking deleted');
        fetchBookings();
        setShowDeleteModal(null);
      } else {
        toast.error('Failed to delete booking');
      }
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  const getStatusColor = (status) => {
    const found = BOOKING_STATUSES.find(s => s.id === status);
    return found?.color || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div data-testid="admin-bookings-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Service Bookings</h1>
          <p className="text-gray-600">Manage service appointments and technician assignments</p>
        </div>
        <button
          onClick={() => fetchBookings()}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="booking-search-input"
              />
            </div>
          </div>
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
              data-testid="booking-status-filter"
            >
              <option value="">All Statuses</option>
              {BOOKING_STATUSES.map(status => (
                <option key={status.id} value={status.id}>{status.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
          <div className="relative">
            <select
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
              data-testid="booking-service-filter"
            >
              <option value="">All Services</option>
              {SERVICE_TYPES.map(service => (
                <option key={service.id} value={service.id}>{service.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
          <div className="relative">
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
              data-testid="booking-city-filter"
            >
              <option value="">All Cities</option>
              {cityOptions.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Search
          </button>
        </form>
      </div>

      {/* Bookings Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No bookings found</h3>
            <p className="text-gray-500">Service bookings will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Booking ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preferred Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Technician</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {bookings.map(booking => (
                  <tr key={booking.id} className="hover:bg-gray-50" data-testid={`booking-row-${booking.id}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="font-medium text-gray-900 text-sm">{booking.id}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{booking.name}</p>
                      <p className="text-sm text-gray-500">{booking.phone}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-sm rounded-lg">
                        <Wrench size={14} />
                        {booking.serviceType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Calendar size={14} />
                        {booking.preferredDate || '-'}
                      </div>
                      {booking.preferredTime && (
                        <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                          <Clock size={14} />
                          {booking.preferredTime}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${getStatusColor(booking.status)}`}>
                        {booking.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">
                        {booking.assigned_technician || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setSelectedBooking(booking)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="View Details"
                          data-testid={`view-booking-${booking.id}`}
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => {
                            setShowStatusModal(booking);
                            setStatusUpdate({
                              status: booking.status,
                              assigned_technician: booking.assigned_technician || '',
                              scheduled_date: booking.scheduled_date || '',
                              scheduled_time: booking.scheduled_time || '',
                              admin_notes: ''
                            });
                          }}
                          className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg"
                          title="Update Status"
                          data-testid={`edit-booking-${booking.id}`}
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => setShowDeleteModal(booking)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Booking Details Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Booking Details</h3>
                <p className="text-sm text-gray-500">{selectedBooking.id}</p>
              </div>
              <button onClick={() => setSelectedBooking(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Status */}
              <div className="flex items-center gap-2">
                <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(selectedBooking.status)}`}>
                  {selectedBooking.status}
                </span>
              </div>

              {/* Customer Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Customer Information</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User size={16} className="text-gray-400" />
                    <span>{selectedBooking.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={16} className="text-gray-400" />
                    <span>{selectedBooking.phone}</span>
                  </div>
                  {selectedBooking.email && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">@</span>
                      <span>{selectedBooking.email}</span>
                    </div>
                  )}
                  {selectedBooking.address && (
                    <div className="flex items-start gap-2">
                      <MapPin size={16} className="text-gray-400 mt-0.5" />
                      <span>{selectedBooking.address}</span>
                    </div>
                  )}
                  {(selectedBooking.city || selectedBooking.area) && (
                    <div className="flex items-start gap-2">
                      <MapPin size={16} className="text-cyan-500 mt-0.5" />
                      <span className="font-medium">
                        {[selectedBooking.area, selectedBooking.city].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                  {selectedBooking.source && (
                    <div className="text-xs text-gray-500 mt-1">
                      Source: <span className="font-mono">{selectedBooking.source}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Service Details */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Service Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service Type</span>
                    <span className="font-medium">{selectedBooking.serviceType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Preferred Date</span>
                    <span>{selectedBooking.preferredDate || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Preferred Time</span>
                    <span>{selectedBooking.preferredTime || '-'}</span>
                  </div>
                  {selectedBooking.assigned_technician && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Assigned Technician</span>
                      <span className="font-medium">{selectedBooking.assigned_technician}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Message */}
              {selectedBooking.message && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Customer Message</h4>
                  <p className="text-sm text-gray-600">{selectedBooking.message}</p>
                </div>
              )}

              {/* Action Button */}
              <button
                onClick={() => {
                  setSelectedBooking(null);
                  setShowStatusModal(selectedBooking);
                  setStatusUpdate({
                    status: selectedBooking.status,
                    assigned_technician: selectedBooking.assigned_technician || '',
                    scheduled_date: selectedBooking.scheduled_date || '',
                    scheduled_time: selectedBooking.scheduled_time || '',
                    admin_notes: ''
                  });
                }}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Update Booking Status</h3>
            <p className="text-sm text-gray-500 mb-4">Booking: {showStatusModal.id}</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={statusUpdate.status}
                  onChange={(e) => setStatusUpdate({...statusUpdate, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="booking-status-update-select"
                >
                  {BOOKING_STATUSES.map(status => (
                    <option key={status.id} value={status.id}>{status.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Technician</label>
                <input
                  type="text"
                  value={statusUpdate.assigned_technician}
                  onChange={(e) => setStatusUpdate({...statusUpdate, assigned_technician: e.target.value})}
                  placeholder="Enter technician name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date</label>
                  <input
                    type="date"
                    value={statusUpdate.scheduled_date}
                    onChange={(e) => setStatusUpdate({...statusUpdate, scheduled_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Time</label>
                  <input
                    type="time"
                    value={statusUpdate.scheduled_time}
                    onChange={(e) => setStatusUpdate({...statusUpdate, scheduled_time: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes (Optional)</label>
                <textarea
                  value={statusUpdate.admin_notes}
                  onChange={(e) => setStatusUpdate({...statusUpdate, admin_notes: e.target.value})}
                  placeholder="Add internal notes..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowStatusModal(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusUpdate}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                data-testid="confirm-booking-status-update"
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Booking?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this booking from {showDeleteModal.name}? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteModal.id)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminBookings;
